import { Root } from '@studio/design-system/button';
import { Checkbox } from '@studio/design-system/checkbox';
import type { HtmlAttributesWithChildren } from '@studio/design-system/props';
import { Icon } from '@studio/design-system/sorting';
import { reverse } from '@studio/js-utils/sorting';
import { useEffect, useRef, type ReactElement } from 'react';
import { twMerge } from 'cn';
import { Layout } from './constants.js';
import type { HeaderProps } from './types.js';

// Brand column added so fingerprint-matched discoveries can surface
// the vendor explicitly. The Model value collapses back into the
// Sources label as "{Model} ({IP})" since duplicating it in a
// dedicated column was redundant. HeaderField.key must stay aligned
// with the `columns` array in table.tsx and the grid-cols track count
// in constants.ts Layout.row — all three define the same ordered slot
// set.
type HeaderField = {
  key: 'name' | 'ip' | 'protocol' | 'brand' | 'bridge';
  label: string;
  sortKey?: 'name' | 'protocol' | 'ip' | 'brand' | 'bridge';
  showCount?: boolean;
};

const HEADER_FIELDS: HeaderField[] = [
  {
    // "Sources (N)" — the count rides in this column header. Shared across the
    // video/audio pickers and the controllers list; all surface the count here.
    // (ENG-4433's video add-source top bar briefly relabeled this column to
    // "Name" via a `nameLabel` prop; ENG-4485 removed that top bar, so the
    // column is back to the shared default everywhere.) Per
    // `reference-rules-in-comments`.
    key: 'name',
    label: 'Sources',
    sortKey: 'name',
    showCount: true
  },
  {
    key: 'ip',
    label: 'IP',
    sortKey: 'ip'
  },
  {
    key: 'protocol',
    label: 'Protocol',
    sortKey: 'protocol'
  },
  {
    key: 'brand',
    label: 'Brand',
    sortKey: 'brand'
  },
  {
    key: 'bridge',
    label: 'Bridge',
    sortKey: 'bridge'
  }
];

/** @internal */
const H = ({ children, className, ...props }: HtmlAttributesWithChildren): ReactElement => (
  <Root
    className={twMerge('h-40 text-left font-demibold', className)}
    role='columnheader'
    {...props}
  >
    <div className='flex items-center gap-2'>{children}</div>
  </Root>
);

/** `<Header>` component. */
export const Header = ({
  className,
  count,
  sorting,
  multiSelectAll,
  showBrand = true,
  ...props
}: HeaderProps): ReactElement => {
  const { key, order } = sorting.value;

  // Radix Checkbox does not expose `indeterminate` via state; it sets
  // `data-state="indeterminate"` only when `checked === 'indeterminate'`.
  // We pass that sentinel value when indeterminate is true so the
  // visual state matches and screen readers see the tri-state.
  const selectAllChecked = multiSelectAll
    ? multiSelectAll.indeterminate
      ? 'indeterminate'
      : multiSelectAll.checked
    : false;

  // Sync the underlying input's `indeterminate` property as a defense in
  // depth — some assistive tech reads it directly even when Radix has set
  // data-state. The ref points at the hidden input that Radix renders.
  const checkboxRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!checkboxRef.current) return;
    const input = checkboxRef.current.querySelector<HTMLInputElement>('input');
    if (input) input.indeterminate = !!multiSelectAll?.indeterminate;
  }, [multiSelectAll?.indeterminate]);

  // Hide the Brand header when showBrand is false; the grid track below
  // drops the brand column so the remaining headers stay aligned.
  const fields = showBrand ? HEADER_FIELDS : HEADER_FIELDS.filter((f) => f.key !== 'brand');

  // Pick the grid layout from the two independent toggles. The checkbox
  // variants prepend a leading 20px track for the select-all cell; without
  // `multiSelectAll` the header does not reserve that gutter.
  const layoutRow = multiSelectAll
    ? showBrand
      ? Layout.row
      : Layout.rowWithCheckboxNoBrand
    : showBrand
      ? Layout.rowNoCheckbox
      : Layout.rowNoBrand;

  return (
    <div className={twMerge(layoutRow, 'px-6', className)} role='rowheader' {...props}>
      {/* Checkbox cell — rendered only when the caller wired the
          select-all handler. In single-add (no `multiSelectAll`) the
          cell + its 20px track are omitted entirely. */}
      {multiSelectAll && (
        <div className='flex items-center justify-center'>
          <Checkbox.Root
            ref={checkboxRef}
            checked={selectAllChecked}
            onCheckedChange={() => multiSelectAll.onChange()}
            aria-label='Select all sources'
            className='flex h-16 w-16 items-center justify-center rounded-2 border-1 border-[#A9B3C6] data-[state=checked]:bg-primary data-[state=indeterminate]:bg-primary'
          >
            <Checkbox.Indicator>
              {/* Distinct icon for indeterminate (dash) vs checked (tick); Radix
                  toggles data-state so we render both and let CSS hide one. */}
              <i
                aria-hidden
                className={selectAllChecked === 'indeterminate' ? 'i-mdi-minus' : 'i-mdi-tick'}
              />
            </Checkbox.Indicator>
          </Checkbox.Root>
        </div>
      )}
      {fields.map(({ key: fieldKey, label, sortKey, showCount }) => (
        <H
          key={fieldKey}
          onClick={(): void => {
            if (!sortKey) return;
            sorting({ key: sortKey, order: key === sortKey ? reverse(order) : order });
          }}
        >
          {/* The count-bearing column renders "Sources (N)"; other columns
              render their bare label (showCount === false). */}
          <span>{showCount ? `${label} (${count})` : label}</span>
          {key === sortKey && (!showCount || !!count) && <Icon order={order} />}
        </H>
      ))}
    </div>
  );
};
